<script setup lang="ts">
import { useField, useFieldValue } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import { useElementalForm } from '../ElementalFormContextProvider/context'
import FormSection from '../FormSection/index.vue'
import { useContainerSize } from '@/composables/useContainerSize'
import { ElementalTypeEnum } from '~~/src/types/elemental'
import { useScrollTo } from '@/composables/useScrollTo'

defineEmits<Emits>()

interface Emits {
  (event: 'cancel'): void
  (event: 'submit'): void
}

const { elementalType, dispatchChange } = useElementalForm()
const { colors } = useTheme()
const { isSmall } = useContainerSize()
const { scrollTo } = useScrollTo()
const toast = useToast()

const { value: isPrivate, setValue: setIsPrivate } = useField<boolean>('isPrivate')
const { value: templateValue } = useField<string>('template')
const topicIds = useFieldValue<number[]>('topicIds')

const isPrivateModel = ref(isPrivate.value)

const hasTemplateContent = computed(() => {
  return templateValue.value && templateValue.value.trim().length > 0
})

const hasTopics = computed(() => {
  return topicIds.value && topicIds.value.length > 0
})

const isPrompt = computed(() => unref(elementalType)?.id === ElementalTypeEnum.Prompt)

const canMakePublic = computed(() => {
  return !isPrompt.value || (hasTemplateContent.value && hasTopics.value)
})

const shouldShowPrivacySection = computed(
  () =>
    getElementalSection(unref(elementalType), 'isPrivate')
    && unref(elementalType)?.id !== ElementalTypeEnum.Cell,
)

watch(isPrivate, (newValue) => {
  isPrivateModel.value = newValue
}, { immediate: true })

const handleCheckboxChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  const newValue = target.checked

  if (!newValue && !canMakePublic.value) {
    e.preventDefault()
    target.checked = isPrivateModel.value

    const errors: string[] = []

    if (!hasTemplateContent.value) {
      errors.push('• Content is required to make public')
      scrollTo('.tiptap')
    }

    if (!hasTopics.value)
      errors.push('• At least one topic is required to make public')

    if (errors.length > 0)
      toast.error(['Cannot make public', ...errors].join('\n'))

    return
  }

  isPrivateModel.value = newValue
  setIsPrivate(newValue)

  dispatchChange('isPrivate')
}

const elementName = computed(() =>
  capitalizeFirstLetter(elementalType?.value?.name || 'prompt'),
)
</script>

<template>
  <FormSection section-id="final">
    <div
      v-if="shouldShowPrivacySection"
      class="flex items-start mb-3 gap-2 max-w-[320px]"
    >
      <TextV2 class="color-darker-gray pt-[2px]">
        <div i-mdi:alert-octagon-outline />
      </TextV2>
      <Text variant="caption" class="color-darker-gray">
        Be mindful we don't take responsibility for any actions taken by third
        parties based on the information you decide to disclose. Avoid sharing
        any sensitive information.
      </Text>
    </div>

    <div
      class="flex flex-col md:flex-row gap-4 justify-between items-center"
      :class="{
        'flex-col! items-start!': isSmall,
      }"
    >
      <div v-if="shouldShowPrivacySection">
        <FormCheckbox
          :model-value="isPrivateModel"
          :disabled="!canMakePublic && !isPrivateModel"
          @change="handleCheckboxChange"
        >
          <div class="flex items-center gap-1 relative w-max">
            <HeadingV2 level="6">
              This {{ elementName }} is Unlisted
            </HeadingV2>

            <ClientOnly>
              <Popper hover placement="top-start">
                <div i-material-symbols:help-outline class="cursor-pointer" />

                <template #content>
                  <AppFloatingMenu
                    class="top-0 left-full w-full max-w-[280px] p-4 rounded-3"
                  >
                    <div class="flex flex-col gap-3">
                      <div class="flex justify-between">
                        <Text variant="small" text-bluish-darker bold>
                          Unlisted
                        </Text>

                        <IconHide
                          :size="16"
                          :stroke-color="colors.bluishDarker"
                        />
                      </div>
                      <Text variant="small" text-bluish-darker>
                        Toggle to make this prompt unlisted. Only those with the
                        link can view it. You can switch to public anytime.
                      </Text>
                      <Text
                        v-if="!canMakePublic && !isPrivateModel"
                        variant="small"
                        text-red-500
                      >
                        You must provide template content AND select at least
                        one topic before making this public.
                      </Text>
                    </div>
                  </AppFloatingMenu>
                </template>
              </Popper>
            </ClientOnly>
          </div>
        </FormCheckbox>
      </div>
    </div>
  </FormSection>
</template>
